import { useState, useCallback, useContext } from "react";
import { Box, Button, Menu, Chip, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Text from "components/common/Typography/Text";
import { useCompletedRefreshState, retryRefreshForPatient, isRetryableRefresh } from "hooks/usePatientDataRefresh";
import type { CompletedRefresh } from "hooks/usePatientDataRefresh";
import CompletedProcessItem from "./CompletedProcessItem";
import { useQueryClient } from "@tanstack/react-query";
import { useWebSocket } from "contexts/WebSocketContext";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import { falseRefreshFailuresSuppressFeature, isFeatureEnabled } from "utils/featureFlags";

export const RecentActivityDropdown = () => {
  const theme = useTheme();
  const completedProcesses = useCompletedRefreshState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const queryClient = useQueryClient();
  const { isConnected: isWebSocketConnected } = useWebSocket();
  const flagsData = useContext(FeatureFlagsContext);
  const falseFailuresSuppressed = isFeatureEnabled(flagsData, falseRefreshFailuresSuppressFeature);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRetry = useCallback(
    (process: CompletedRefresh) => {
      retryRefreshForPatient(process.patientId, process.patientName, queryClient, isWebSocketConnected, falseFailuresSuppressed);
    },
    [queryClient, isWebSocketConnected, falseFailuresSuppressed]
  );

  const open = Boolean(anchorEl);
  const failedCount = completedProcesses.filter((p) => isRetryableRefresh(p, falseFailuresSuppressed)).length;

  // Don't render if no completed processes
  if (completedProcesses.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClick}
        variant="text"
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: theme.palette.text.secondary,
          textTransform: "none",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}>
        <Text variant="body2" sx={{ mr: 0.5 }}>
          Recent Activity
        </Text>
        {completedProcesses.length > 0 && (
          <Chip
            label={completedProcesses.length}
            size="small"
            sx={{
              height: "20px",
              minWidth: "20px",
              backgroundColor: failedCount > 0 ? theme.palette.error.main : theme.palette.success.main,
              color: theme.palette.common.white,
              fontSize: "12px",
              fontWeight: 600,
              "& .MuiChip-label": {
                padding: "0 6px",
              },
            }}
          />
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 400,
            maxWidth: 500,
            maxHeight: 500,
            borderRadius: "8px",
            boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
          },
        }}>
        <Box sx={{ px: "20px", pb: "20px", pt: "12px" }}>
          <Text variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: "16px" }}>
            Recent Activity
          </Text>
          {completedProcesses.length === 0 ? (
            <Text variant="body2" sx={{ color: theme.palette.text.secondary, py: 2, textAlign: "center" }}>
              No recent activity
            </Text>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {completedProcesses.map((process, index) => (
                <CompletedProcessItem
                  key={`${process.processId}-${process.completedAt}`}
                  process={process}
                  onClose={handleClose}
                  onRetry={isRetryableRefresh(process, falseFailuresSuppressed) ? () => handleRetry(process) : undefined}
                  isRetryable={isRetryableRefresh(process, falseFailuresSuppressed)}
                  isLast={index === completedProcesses.length - 1}
                />
              ))}
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default RecentActivityDropdown;
